/**
 * 题库下载服务
 * 封装本地存储和下载管理逻辑
 */

import {
  BankStorage,
  DownloadManager,
  type BankIndexItem,
  type LocalBank,
  type Question,
  type DownloadProgress,
  type BankUpdate,
  type ApiClient,
  type BankDownloadData,
} from '@examflow/core';
import { banks as banksApi } from './api';
import { DexieBackend } from '../data/local';
import { bankAudioCache } from '../data/local/bankAudioCache';

// 生成设备 ID
function getDeviceId(): string {
  const key = 'examflow_device_id';
  let deviceId = localStorage.getItem(key);
  if (!deviceId) {
    deviceId = `web-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
    localStorage.setItem(key, deviceId);
  }
  return deviceId;
}

// API 客户端适配器
const apiClientAdapter: ApiClient = {
  async getBanks() {
    const result = await banksApi.list();
    return {
      banks: result.banks.map((b) => ({
        id: b.id,
        name: b.name,
        version: b.version,
        access: b.access || { hasAccess: false },
      })),
    };
  },

  async downloadBank(
    bankId: string,
    deviceId?: string
  ): Promise<{ success: boolean; data: BankDownloadData }> {
    const result = await banksApi.download(bankId, deviceId);
    return {
      success: true,
      data: result as unknown as BankDownloadData,
    };
  },

  async checkBankUpdate(bankId: string, currentVersion: string) {
    const result = await banksApi.checkUpdate(bankId, currentVersion);
    return {
      success: true,
      data: result,
    };
  },

  async syncBank(bankId: string, currentVersion: string) {
    const result = await banksApi.sync(bankId, currentVersion);
    return { success: true, data: result as BankDownloadData & { deletedQuestionIds?: string[] } };
  },
};

// 单例实例
let storageInstance: BankStorage | null = null;
let downloadManagerInstance: DownloadManager | null = null;
let backendInstance: DexieBackend | null = null;

function getStorageBackend(): DexieBackend {
  if (!backendInstance) {
    backendInstance = new DexieBackend();
  }
  return backendInstance;
}

/**
 * 获取存储实例
 * 使用 Dexie (IndexedDB) 作为存储后端
 */
export function getBankStorage(): BankStorage {
  if (!storageInstance) {
    storageInstance = new BankStorage(getStorageBackend());
  }
  return storageInstance;
}

/**
 * 获取下载管理器实例
 */
export function getDownloadManager(): DownloadManager {
  if (!downloadManagerInstance) {
    downloadManagerInstance = new DownloadManager({
      apiClient: apiClientAdapter,
      deviceId: getDeviceId(),
      storageBackend: getStorageBackend(),
    });
  }
  return downloadManagerInstance;
}

/**
 * 题库服务
 */
export const bankService = {
  /**
   * 获取已下载的题库列表
   */
  async getDownloadedBanks(): Promise<BankIndexItem[]> {
    const storage = getBankStorage();
    return storage.getDownloadedBanks();
  },

  /**
   * 获取本地题库数据
   */
  async getLocalBank(bankId: string): Promise<LocalBank | null> {
    const storage = getBankStorage();
    return storage.getBank(bankId);
  },

  /**
   * 按题目 ID 从多个题库加载题目
   * @param bankIds 题库 ID 列表
   * @param questionIds 题目 ID 列表（保持顺序）
   * @returns 按 questionIds 顺序排列的 (Question | null)[]，未找到的为 null
   */
  async getQuestionsByIds(bankIds: string[], questionIds: string[]): Promise<(Question | null)[]> {
    const idSet = new Set(questionIds);
    const questionMap = new Map<string, Question>();

    for (const bankId of bankIds) {
      if (questionMap.size >= idSet.size) break;
      const bank = await this.getLocalBank(bankId);
      if (!bank?.questions) continue;
      for (const q of bank.questions) {
        if (idSet.has(q.id) && !questionMap.has(q.id)) {
          questionMap.set(q.id, q);
        }
      }
    }

    return questionIds.map((id) => questionMap.get(id) ?? null);
  },

  /**
   * 检查题库是否已下载
   */
  async isDownloaded(bankId: string): Promise<boolean> {
    const storage = getBankStorage();
    return storage.isDownloaded(bankId);
  },

  /**
   * 下载题库
   * @param bankId 题库ID
   * @param bankName 题库名称
   * @param onProgress 进度回调
   * @param forceDownload 是否强制下载（跳过版本检查），默认 false
   */
  async downloadBank(
    bankId: string,
    bankName: string,
    onProgress?: (progress: DownloadProgress) => void,
    forceDownload: boolean = false
  ): Promise<boolean> {
    const manager = getDownloadManager();

    // 注册进度回调
    let unsubscribe: (() => void) | undefined;
    if (onProgress) {
      unsubscribe = manager.onProgress(onProgress);
    }

    try {
      const success = await manager.downloadBank(bankId, bankName, forceDownload);
      return success;
    } finally {
      if (unsubscribe) {
        unsubscribe();
      }
    }
  },

  async ensureBank(
    bankId: string,
    bankName: string,
    expectedVersion?: string,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<boolean> {
    const manager = getDownloadManager();
    const unsubscribe = onProgress ? manager.onProgress(onProgress) : undefined;
    try {
      const ok = await manager.ensureBank(bankId, bankName, expectedVersion);
      const deleted = manager.getLastDeletedQuestionIds();
      if (deleted.length > 0) {
        await bankAudioCache.deleteByQuestionIds(deleted);
      }
      return ok;
    } finally {
      unsubscribe?.();
    }
  },

  /**
   * 取消下载
   */
  cancelDownload(bankId: string): void {
    const manager = getDownloadManager();
    manager.cancelDownload(bankId);
  },

  /**
   * 删除本地题库
   */
  async deleteBank(bankId: string): Promise<void> {
    const storage = getBankStorage();
    await storage.deleteBank(bankId);
  },

  /**
   * 检查单个题库更新
   */
  async checkBankUpdate(bankId: string): Promise<BankUpdate | null> {
    const manager = getDownloadManager();
    return manager.checkBankUpdate(bankId);
  },

  /**
   * 检查所有已下载题库的更新
   */
  async checkAllUpdates(): Promise<BankUpdate[]> {
    const manager = getDownloadManager();
    return manager.checkAllUpdates();
  },

  /**
   * 更新题库（重新下载）
   */
  async updateBank(
    bankId: string,
    onProgress?: (progress: DownloadProgress) => void
  ): Promise<boolean> {
    const manager = getDownloadManager();
    const storage = getBankStorage();

    const meta = await storage.getBankMeta(bankId);

    let unsubscribe: (() => void) | undefined;
    if (onProgress) {
      unsubscribe = manager.onProgress(onProgress);
    }

    try {
      // 更新操作应该强制下载，跳过版本检查
      return await manager.downloadBank(bankId, meta?.name, true);
    } finally {
      if (unsubscribe) {
        unsubscribe();
      }
    }
  },

  /**
   * 获取下载状态
   */
  getDownloadState(bankId: string): DownloadProgress | undefined {
    const manager = getDownloadManager();
    return manager.getDownloadState(bankId);
  },

  /**
   * 注册全局进度回调
   */
  onProgress(callback: (progress: DownloadProgress) => void): () => void {
    const manager = getDownloadManager();
    return manager.onProgress(callback);
  },

  /**
   * 获取存储使用情况
   */
  async getStorageUsage(): Promise<{
    totalSize: number;
    banks: Array<{ id: string; name: string; size: number }>;
  }> {
    const storage = getBankStorage();
    return storage.getStorageUsage();
  },

  /**
   * 自动检查更新
   * @param interval 检查间隔（毫秒），默认30分钟。如果为0或负数，则只检查一次
   * @param onUpdate 发现更新时的回调函数
   * @returns 清理函数，用于停止自动检查
   */
  autoCheckUpdates(
    interval: number = 30 * 60 * 1000, // 默认30分钟
    onUpdate?: (updates: BankUpdate[]) => void
  ): () => void {
    let intervalId: number | null = null;
    let isRunning = true;

    const checkUpdates = async () => {
      if (!isRunning) return;

      try {
        const updates = await this.checkAllUpdates();
        if (updates.length > 0 && onUpdate) {
          onUpdate(updates);
        }
      } catch (error) {
        console.error('自动检查更新失败:', error);
      }
    };

    // 立即检查一次
    checkUpdates();

    // 如果设置了间隔，则定时检查
    if (interval > 0) {
      intervalId = window.setInterval(checkUpdates, interval);
    }

    // 返回清理函数
    return () => {
      isRunning = false;
      if (intervalId !== null) {
        clearInterval(intervalId);
      }
    };
  },

  /**
   * 在应用启动时检查更新（一次性检查）
   * @param onUpdate 发现更新时的回调函数
   */
  async checkUpdatesOnStartup(onUpdate?: (updates: BankUpdate[]) => void): Promise<BankUpdate[]> {
    try {
      const updates = await this.checkAllUpdates();
      if (updates.length > 0 && onUpdate) {
        onUpdate(updates);
      }
      return updates;
    } catch (error) {
      console.error('启动时检查更新失败:', error);
      return [];
    }
  },
};

export default bankService;
